import type { EnvironmentType } from '@post-office/shared-contracts/execution-runtime';
import type { RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context/requestContext';
import { type BackendMessageBody } from '@post-office/placement-common-v2/types';
import { type SerializableRecord } from '@post-office/serializable-record';
import { getJiraOnboardingUrl } from '@post-office/message-templates-common--growth-starburst/utils/get-jira-onboarding-url';
import { type RedirectTo } from '@post-office/message-templates-common--growth-starburst/utils/resolve-redirect-to';

import { getConfluenceOnboardingUrl } from '../placements/in-app-v2/confluence-side-nav/get-confluence-onboarding-url';

export {
	resolveRedirectTo,
	type RedirectTo,
} from '@post-office/message-templates-common--growth-starburst/utils/resolve-redirect-to';

const LOOM_ONBOARDING_URL_PROD = 'https://www.loom.com/meetings';
const LOOM_ONBOARDING_URL_NON_PROD = 'https://stage.loom.com/meetings';

// Unified Rovo create surface — tenant-agnostic, AI-first Confluence create experience.
// Used by the missing-app flow (where the intent is "try Confluence", not "go to your
// Confluence tenant"). The generic TWCG-991 flow continues to use the tenant-aware
// onboarding helper that respects the user's actual Confluence cloudId.
const CONFLUENCE_CREATE_WITH_ROVO_URL = 'https://create.rovo.com';

export const getLoomOnboardingUrl = (environmentType: EnvironmentType): string => {
	return environmentType === 'Production' ? LOOM_ONBOARDING_URL_PROD : LOOM_ONBOARDING_URL_NON_PROD;
};

/**
 * Which banner flow is requesting the onboarding URL.
 *
 * - `generic`     — TWCG-991 / legacy generic activation banner. Confluence routes to
 *                   the user's own tenant via TCS/TAP-resolved cloudId.
 * - `missing-app` — TWCG missing-app personalized banner. Confluence routes to the
 *                   tenant-agnostic Rovo create surface (intent-based, not tenant-based).
 */
export type BannerFlow = 'generic' | 'missing-app';

type GetOnboardingUrlParams = {
	redirectTo: RedirectTo;
	flow: BannerFlow;
	requestContext: RequestContext;
	messageInstanceId: string;
	messageTemplateId: string;
	orgId?: string;
	twcEntitlementId?: string;
	triggerId?: string;
};

/**
 * Returns the onboarding URL for the banner CTA, routing per `redirectTo` and `flow`.
 *
 * Routing matrix:
 *   redirectTo \ flow │ generic                                    │ missing-app
 *   ──────────────────┼────────────────────────────────────────────┼──────────────────────────────
 *   jira              │ getJiraOnboardingUrl (GUPS, tenant-aware)  │ getJiraOnboardingUrl (same)
 *   confluence        │ getConfluenceOnboardingUrl (tenant-aware)  │ https://create.rovo.com
 *
 * Note: `loom` is not handled here — callers use `getLoomOnboardingUrl` directly because
 * the destination depends only on environment, not on request context or flow.
 */
export const getOnboardingUrl = async ({
	redirectTo,
	flow,
	requestContext,
	messageInstanceId,
	messageTemplateId,
	orgId,
	twcEntitlementId,
	triggerId,
}: GetOnboardingUrlParams): Promise<string | undefined> => {
	switch (redirectTo) {
		case 'jira':
			return getJiraOnboardingUrl(
				requestContext,
				orgId,
				twcEntitlementId,
				messageInstanceId,
				messageTemplateId,
			);

		case 'confluence':
			if (flow === 'missing-app') {
				return CONFLUENCE_CREATE_WITH_ROVO_URL;
			}
			return getConfluenceOnboardingUrl(requestContext, {
				messageTemplateId,
				messageInstanceId,
				triggerId: triggerId ?? '',
			} as BackendMessageBody<SerializableRecord>);

		default: {
			const _exhaustive: never = redirectTo;
			return _exhaustive;
		}
	}
};
